'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { PRICING_TOURNAMENT } from '@chalk/shared/pricing'
import { MarketingDisplayH1 } from '@/components/marketing/MarketingDisplayH1'
import { MarketingNav } from '@/components/marketing/MarketingNav'
import { HomepageFooter } from '@/components/marketing/HomepageFooter'
import HeroBackground from '@/components/marketing/HeroBackgroundDynamic'

// ── Web app window frame (no phone — director portal is desktop-first) ────────

function BrowserFrame({ active, children }: { active: string; children: React.ReactNode }) {
  const navItems = ['Overview', 'Schedule', 'Tryouts', 'Finances', 'Compliance', 'Settings']
  return (
    <div className="w-full rounded-2xl overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.18)', border: '1px solid #E2E8F0', maxWidth: 780 }}>
      {/* Browser chrome */}
      <div className="h-9 flex items-center px-4 gap-3 shrink-0" style={{ background: '#F1F3F6', borderBottom: '1px solid #E2E8F0' }}>
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <div className="w-3 h-3 rounded-full bg-[#FFBC2E]" />
          <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
        </div>
        <div className="flex-1 mx-4">
          <div className="bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-medium" style={{ border: '1px solid #E2E8F0' }}>
            org.chalksports.ai / <span className="text-app-card">Directors Portal</span>
          </div>
        </div>
      </div>
      {/* App shell */}
      <div className="flex" style={{ height: 460 }}>
        {/* Sidebar */}
        <div className="w-44 shrink-0 flex flex-col" style={{ background: '#071428', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="text-base font-black text-white mb-2.5" style={{ fontFamily: 'var(--font-caveat)' }}>
              chalk<span style={{ color: '#F5E642' }}>.</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center text-[8px] font-black text-chalk-dark shrink-0" style={{ background: '#F5E642' }}>EL</div>
              <div>
                <div className="text-[9px] font-bold text-white leading-none">Elite Baseball</div>
                <div className="text-[8px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Club Director</div>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-2 py-3 space-y-0.5">
            <div className="text-[7px] font-bold uppercase tracking-widest px-2 mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>Organization</div>
            {navItems.map(item => {
              const isActive = item === active
              return (
                <div key={item} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[10px] font-medium" style={{ background: isActive ? 'rgba(245,230,66,0.12)' : 'transparent', color: isActive ? '#F5E642' : 'rgba(255,255,255,0.45)' }}>
                  {item}
                </div>
              )
            })}
          </nav>
          <div className="px-2 pb-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>JR</div>
              <div className="text-[9px] truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>director@elite.com</div>
            </div>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 bg-slate-50 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Sticky sub-nav ─────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'treasurer-pulse', label: 'Treasurer Pulse' },
  { id: 'dashboard',     label: 'Dashboard'     },
  { id: 'finances',      label: 'Finances'      },
  { id: 'tryouts',       label: 'Tryouts'       },
  { id: 'tournaments',   label: 'Tournaments'   },
  { id: 'embed',         label: 'Embed & API'   },
  { id: 'club-plus',     label: 'Club Plus'     },
]

function SubNav() {
  const [active, setActive] = useState('dashboard')
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id) }),
      { rootMargin: '-40% 0px -50% 0px' }
    )
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el) })
    return () => obs.disconnect()
  }, [])
  return (
    <div className="sticky z-40 bg-white border-b border-[#EAECF0]" style={{ top: 64 }}>
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="flex gap-1 overflow-x-auto scrollbar-none py-2">
          {SECTIONS.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="shrink-0 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all whitespace-nowrap"
              style={{
                background: active === s.id ? '#071428' : 'transparent',
                color: active === s.id ? 'white' : 'rgba(7,20,40,0.45)',
              }}
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Section layout ─────────────────────────────────────────────────────────────

function Section({
  id, eyebrow, heading, desc, bullets, cta, ctaHref = '/org/signup', flip = false,
  hero = false, demo,
}: {
  id: string; eyebrow: string; heading: string; desc: string
  bullets: string[]; cta: string; ctaHref?: string; flip?: boolean
  hero?: boolean; demo: React.ReactNode
}) {
  return (
    // `hero` = the Category-C lead (Treasurer Pulse): an evergreen-tinted band +
    // larger heading + evergreen eyebrow so it breaks the uniform section rhythm.
    <section id={id} className="py-24" style={hero ? { background: 'linear-gradient(180deg, rgba(15,82,50,0.06) 0%, transparent 65%)' } : undefined}>
      <div className="max-w-[1100px] mx-auto px-6">
        <div className={`flex flex-col lg:flex-row items-center gap-16 ${flip ? 'lg:flex-row-reverse' : ''}`}>
          {/* Copy */}
          <div className={`${hero ? 'lg:w-[400px]' : 'lg:w-[340px]'} shrink-0`}>
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest mb-5" style={{ background: hero ? 'rgba(15,82,50,0.12)' : 'rgba(7,20,40,0.06)', color: hero ? '#0F5132' : '#071428' }}>
              {eyebrow}
            </div>
            <h2 className={`font-black text-chalk-dark mb-4 leading-tight tracking-tight ${hero ? 'text-[40px] sm:text-[44px]' : 'text-3xl'}`}>{heading}</h2>
            <p className="text-slate-500 leading-relaxed mb-6 text-[15px]">{desc}</p>
            <ul className="space-y-2.5 mb-8">
              {bullets.map(b => (
                <li key={b} className="flex items-start gap-2.5 text-[14px] text-slate-700">
                  <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(245,230,66,0.3)' }}>
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2 3-3" stroke="#5a4a00" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <Link href={ctaHref} className="inline-flex items-center gap-2 bg-chalk-dark text-white text-[13px] font-bold rounded-full px-5 py-2.5 hover:bg-chalk-dark transition-colors">
              {cta} →
            </Link>
          </div>

          {/* Demo */}
          <div className="flex-1 w-full flex justify-center lg:justify-end">
            {demo}
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Dashboard demo ─────────────────────────────────────────────────────────────

function DashboardDemo() {
  const teams = [
    { name: '14U Elite',   sport: 'Baseball', players: 14, next: 'Sat 9:00am' },
    { name: '12U Select',  sport: 'Baseball', players: 13, next: 'Sun 10:30am' },
    { name: '10U Rookies', sport: 'Baseball', players: 11, next: 'Sat 8:00am' },
    { name: '16U Gold',    sport: 'Softball', players: 15, next: 'Fri 6:00pm' },
  ]
  const stats = [
    { label: 'Active Teams',     value: '8'     },
    { label: 'Total Athletes',   value: '112'   },
    { label: 'Revenue (YTD)',    value: '$41.2k' },
    { label: 'Dues Collected',   value: '93%'   },
  ]
  return (
    <BrowserFrame active="Overview">
      <div className="p-5 h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[13px] font-black text-app-card">Good morning, James</div>
            <div className="text-[10px] text-slate-400">Elite Baseball · Spring 2026</div>
          </div>
          <div className="bg-chalk-yellow-hover text-chalk-dark text-[9px] font-bold rounded-full px-3 py-1.5">+ New Team</div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {stats.map(s => (
            <div key={s.label} className="bg-white rounded-xl p-3" style={{ border: '1px solid #F0F2F5' }}>
              <div className="text-[14px] font-black text-app-card leading-none">{s.value}</div>
              <div className="text-[9px] text-slate-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        {/* Teams */}
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Your Teams</div>
        <div className="space-y-2">
          {teams.map(t => (
            <div key={t.name} className="bg-white rounded-xl px-4 py-3 flex items-center gap-4" style={{ border: '1px solid #F0F2F5' }}>
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-[8px] font-black text-chalk-dark shrink-0" style={{ background: '#F5E642' }}>{t.name.slice(0, 2)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-app-card truncate">{t.name}</div>
                <div className="text-[9px] text-slate-400">{t.sport} · {t.players} players</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] font-semibold text-app-card">{t.next}</div>
                <div className="text-[8px] text-slate-400">next game</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ── Tryouts demo ───────────────────────────────────────────────────────────────

function TryoutsDemo() {
  const [selected, setSelected] = useState<string | null>(null)
  const prospects = [
    { name: 'Marcus T.', age: '14', pos: 'SS / P', speed: 88, arm: 85, hit: 78, overall: 84, status: 'offer' },
    { name: 'Devon K.',  age: '13', pos: '2B',     speed: 74, arm: 80, hit: 91, overall: 82, status: 'waitlist' },
    { name: 'Ryan M.',   age: '14', pos: 'OF',     speed: 92, arm: 70, hit: 69, overall: 77, status: 'review' },
    { name: 'Cole B.',   age: '14', pos: 'C',      speed: 65, arm: 88, hit: 85, overall: 80, status: 'offer' },
    { name: 'Jaylen S.', age: '13', pos: '1B',     speed: 71, arm: 75, hit: 88, overall: 78, status: 'review' },
  ]
  const statusColors: Record<string, string> = { offer: '#22c55e', waitlist: '#f59e0b', review: '#94a3b8' }
  const statusLabels: Record<string, string> = { offer: 'Offer sent', waitlist: 'Waitlist', review: 'In review' }
  return (
    <BrowserFrame active="Tryouts">
      <div className="h-full flex flex-col">
        <div className="px-5 py-3 bg-white flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F5' }}>
          <div>
            <div className="text-[12px] font-black text-app-card">14U Elite Tryouts</div>
            <div className="text-[10px] text-slate-400">May 18 · 47 registered · 5 evaluated</div>
          </div>
          <div className="bg-chalk-dark text-white text-[9px] font-bold rounded-lg px-3 py-1.5">Export PDF</div>
        </div>
        <div className="flex flex-1 overflow-hidden">
          {/* Prospect list */}
          <div className="w-52 shrink-0 overflow-y-auto" style={{ borderRight: '1px solid #F0F2F5' }}>
            {prospects.map(p => (
              <div
                key={p.name}
                onClick={() => setSelected(p.name)}
                className="px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors"
                style={{ background: selected === p.name ? '#F0F4FF' : 'white', borderBottom: '1px solid #F5F7FA' }}
              >
                <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-[9px] font-black text-indigo-800 shrink-0">
                  {p.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-app-card truncate">{p.name}</div>
                  <div className="text-[9px] text-slate-400">{p.pos} · Age {p.age}</div>
                </div>
                <div className="text-[13px] font-black" style={{ color: p.overall >= 82 ? '#22c55e' : '#f59e0b' }}>{p.overall}</div>
              </div>
            ))}
          </div>
          {/* Detail panel */}
          <div className="flex-1 overflow-y-auto p-4">
            {selected ? (() => {
              const p = prospects.find(x => x.name === selected)!
              return (
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-[14px] font-black text-app-card">{p.name}</div>
                      <div className="text-[10px] text-slate-400">{p.pos} · Age {p.age}</div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold" style={{ background: `${statusColors[p.status]}20`, color: statusColors[p.status] }}>
                      {statusLabels[p.status]}
                    </div>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    {[['Speed', p.speed], ['Arm Strength', p.arm], ['Hitting', p.hit]].map(([label, val]) => (
                      <div key={label as string}>
                        <div className="flex justify-between text-[10px] mb-1"><span className="text-slate-400">{label}</span><span className="font-bold text-app-card">{val}</span></div>
                        <div className="h-1.5 rounded-full bg-[#F0F2F5]"><div className="h-1.5 rounded-full bg-chalk-yellow-hover" style={{ width: `${val}%` }} /></div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 bg-chalk-dark text-white text-[10px] font-bold rounded-lg py-2">Send Offer</button>
                    <button className="flex-1 bg-[#F0F2F5] text-slate-400 text-[10px] font-bold rounded-lg py-2">Waitlist</button>
                  </div>
                </div>
              )
            })() : (
              <div className="h-full flex items-center justify-center text-[11px] text-slate-400">Select a prospect to view scores</div>
            )}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ── Tournaments demo ───────────────────────────────────────────────────────────

function TournamentsDemo() {
  const [tab, setTab] = useState<'setup' | 'bracket'>('setup')
  const divisions = [
    { name: '10U Open',  teams: 8,  fee: '$225', spots: '3 left' },
    { name: '12U Select', teams: 12, fee: '$275', spots: 'Full' },
    { name: '14U Elite',  teams: 16, fee: '$350', spots: '7 left' },
  ]
  return (
    <BrowserFrame active="Schedule">
      <div className="h-full flex flex-col">
        <div className="px-5 py-3 bg-white" style={{ borderBottom: '1px solid #F0F2F5' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[12px] font-black text-app-card">Summer Slam Classic</div>
              <div className="text-[10px] text-slate-400">July 12–14 · Diamond Sports Complex</div>
            </div>
            <div className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: '#dcfce7', color: '#15803d' }}>Published</div>
          </div>
          <div className="flex gap-2">
            {(['setup', 'bracket'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)} className="px-3 py-1.5 rounded-lg text-[10px] font-bold capitalize transition-all" style={{ background: tab === t ? '#071428' : '#F0F2F5', color: tab === t ? 'white' : '#5b6776' }}>
                {t === 'setup' ? 'Divisions' : 'Bracket'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          {tab === 'setup' ? (
            <div className="space-y-3">
              {divisions.map(d => (
                <div key={d.name} className="bg-white rounded-xl p-4" style={{ border: '1px solid #F0F2F5' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[12px] font-bold text-app-card">{d.name}</div>
                      <div className="text-[10px] text-slate-400">{d.teams} team bracket · Entry {d.fee}</div>
                    </div>
                    <div className="text-[9px] font-bold px-2 py-1 rounded-full" style={{ background: d.spots === 'Full' ? '#fee2e2' : '#eff6ff', color: d.spots === 'Full' ? '#dc2626' : '#1d4ed8' }}>{d.spots}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-[#F0F2F5]">
                      <div className="h-1.5 rounded-full bg-chalk-yellow-hover" style={{ width: d.spots === 'Full' ? '100%' : d.spots === '3 left' ? '62%' : '56%' }} />
                    </div>
                    <div className="text-[9px] text-slate-400">{d.spots === 'Full' ? '12/12' : d.spots === '3 left' ? '5/8' : '9/16'}</div>
                  </div>
                </div>
              ))}
              <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between" style={{ border: '1px dashed #C7D7F5' }}>
                <div className="text-[11px] text-slate-400">+ Add division</div>
                <div className="text-[10px] font-bold text-blue-500">$0 setup fee</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">14U Elite · Round Robin → Single Elim</div>
              {[['Game 1', '9:00am', 'Field 3', 'Chosen Gold', 'vs', 'River Dogs'], ['Game 2', '10:30am', 'Field 1', 'Sky Hawks', 'vs', 'River Dogs'], ['Game 3', '12:00pm', 'Field 2', 'Chosen Gold', 'vs', 'Sky Hawks']].map(([g, time, field, t1, , t2]) => (
                <div key={g} className="bg-white rounded-xl p-3 flex items-center gap-3" style={{ border: '1px solid #F0F2F5' }}>
                  <div className="text-center shrink-0">
                    <div className="text-[10px] font-bold text-app-card">{time}</div>
                    <div className="text-[8px] text-slate-400">{field}</div>
                  </div>
                  <div className="flex-1 flex items-center gap-2 text-[11px] font-bold text-app-card">
                    <span className="flex-1 text-right truncate">{t1}</span>
                    <span className="text-[9px] text-slate-400 shrink-0">vs</span>
                    <span className="flex-1 truncate">{t2}</span>
                  </div>
                  <div className="text-[9px] px-2 py-1 rounded-lg shrink-0" style={{ background: '#F0F2F5', color: '#5b6776' }}>Scheduled</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ── Finances demo ──────────────────────────────────────────────────────────────

function FinancesDemo() {
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const teams = [
    { name: '14U Elite',   collected: 3850, total: 4200, pct: 92 },
    { name: '12U Select',  collected: 2860, total: 3575, pct: 80 },
    { name: '10U Rookies', collected: 2310, total: 2310, pct: 100 },
    { name: '16U Gold',    collected: 1890, total: 3375, pct: 56 },
  ]

  const handleReminder = () => {
    setSending(true)
    setTimeout(() => { setSending(false); setSent(true); setTimeout(() => setSent(false), 2500) }, 1200)
  }

  return (
    <BrowserFrame active="Finances">
      <div className="p-5 h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[13px] font-black text-app-card">Revenue Overview</div>
            <div className="text-[10px] text-slate-400">Spring Season · All teams</div>
          </div>
          <button onClick={handleReminder} className="text-[9px] font-bold px-3 py-1.5 rounded-lg transition-all" style={{ background: sending ? '#fef3c7' : sent ? '#dcfce7' : '#071428', color: sending ? '#92400e' : sent ? '#15803d' : 'white' }}>
            {sending ? 'Sending…' : sent ? '✓ Reminders sent' : 'Send payment reminders'}
          </button>
        </div>
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[['Total Collected', '$10,910', '#22c55e'], ['Pending', '$2,550', '#f59e0b'], ['Overdue', '$840', '#ef4444']].map(([label, val, color]) => (
            <div key={label} className="bg-white rounded-xl p-3" style={{ border: '1px solid #F0F2F5' }}>
              <div className="text-[13px] font-black leading-none" style={{ color }}>{val}</div>
              <div className="text-[9px] text-slate-400 mt-1">{label}</div>
            </div>
          ))}
        </div>
        {/* Per-team breakdown */}
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">By Team</div>
        <div className="space-y-2.5">
          {teams.map(t => (
            <div key={t.name} className="bg-white rounded-xl p-3" style={{ border: '1px solid #F0F2F5' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold text-app-card">{t.name}</div>
                <div className="text-[10px] font-black" style={{ color: t.pct === 100 ? '#22c55e' : t.pct >= 80 ? '#f59e0b' : '#ef4444' }}>{t.pct}%</div>
              </div>
              <div className="h-1.5 rounded-full bg-[#F0F2F5] mb-1.5">
                <div className="h-1.5 rounded-full transition-all" style={{ width: `${t.pct}%`, background: t.pct === 100 ? '#22c55e' : t.pct >= 80 ? '#F5E642' : '#ef4444' }} />
              </div>
              <div className="text-[9px] text-slate-400">${t.collected.toLocaleString()} of ${t.total.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ── Embed & API demo ───────────────────────────────────────────────────────────

function EmbedDemo() {
  const [copied, setCopied] = useState(false)
  const snippet = `<script src="https://chalksports.ai/embed.js"></script>
<div data-chalk-widget="schedule"
     data-org="elite-baseball"
     data-theme="light">
</div>`

  const handleCopy = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <BrowserFrame active="Settings">
      <div className="p-5 h-full overflow-y-auto">
        <div className="mb-5">
          <div className="text-[13px] font-black text-app-card mb-1">API & Embed</div>
          <div className="text-[10px] text-slate-400">Publish live schedules and scores on your own website</div>
        </div>
        {/* API key */}
        <div className="bg-white rounded-xl p-4 mb-3" style={{ border: '1px solid #F0F2F5' }}>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">API Key</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2 text-[10px] font-mono text-slate-400" style={{ border: '1px solid #F0F2F5' }}>chk_live_••••••••••••••••••••••••</div>
            <button className="text-[9px] font-bold px-2.5 py-2 rounded-lg bg-[#F0F2F5] text-slate-400">Show</button>
            <button className="text-[9px] font-bold px-2.5 py-2 rounded-lg bg-[#F0F2F5] text-slate-400">Rotate</button>
          </div>
        </div>
        {/* Widget picker */}
        <div className="bg-white rounded-xl p-4 mb-3" style={{ border: '1px solid #F0F2F5' }}>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Embed Widgets</div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {['Schedule', 'Live Score', 'Standings'].map((w, i) => (
              <div key={w} className="rounded-lg p-2.5 text-center cursor-pointer transition-all" style={{ border: `1px solid ${i === 0 ? '#F5E642' : '#F0F2F5'}`, background: i === 0 ? 'rgba(245,230,66,0.08)' : 'white' }}>
                <div className="text-[10px] font-bold text-app-card">{w}</div>
              </div>
            ))}
          </div>
          {/* Code snippet */}
          <div className="rounded-lg overflow-hidden" style={{ background: '#0F172A' }}>
            <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="text-[9px] text-[rgba(255,255,255,0.4)]">Embed code</span>
              <button onClick={handleCopy} className="text-[9px] font-bold px-2 py-1 rounded-md transition-all" style={{ background: copied ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)', color: copied ? '#22c55e' : 'rgba(255,255,255,0.5)' }}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <pre className="px-3 py-2.5 text-[9px] leading-relaxed overflow-x-auto" style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{snippet}</pre>
          </div>
        </div>
        {/* Stats */}
        <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #F0F2F5' }}>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">API Usage (30 days)</div>
          <div className="grid grid-cols-3 gap-3">
            {[['Requests', '14,821'], ['Avg latency', '84ms'], ['Uptime', '99.9%']].map(([l, v]) => (
              <div key={l}>
                <div className="text-[13px] font-black text-app-card">{v}</div>
                <div className="text-[9px] text-slate-400">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ForClubsPage() {
  return (
    <>
      <MarketingNav />

      {/* Hero — 2026-05-21 Category C pivot. Treasurer Pulse leads. Directors
       *  and treasurers are the hero buyer; the daily user is still the coach
       *  but the purchase decision moves to the club layer. */}
      <section data-section="hero" className="relative overflow-hidden pt-32 pb-24 bg-chalkboard-grad">
        <HeroBackground />
        <div className="relative max-w-[1100px] mx-auto px-6 text-center">
          <MarketingDisplayH1 align="center" tone="light" eyebrow="For club directors and treasurers" className="mb-6">
            <span className="block">Close the books.</span>
            <span className="block" style={{ color: '#F5E642' }}>Not the door.</span>
          </MarketingDisplayH1>
          <p className="text-lg text-white/50 max-w-[600px] mx-auto mb-10 leading-relaxed">
            Treasurer Pulse for the people running the club. Multi-team finances, QuickBooks-ready CSV export, and payment-risk signals — under one director portal.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link href="/org/signup" className="bg-chalk-yellow-hover text-chalk-dark text-[15px] font-black rounded-full px-8 py-4 hover:bg-chalk-yellow transition-colors">
              Talk to us about Club Plus →
            </Link>
            <a href="#treasurer-pulse" className="text-white/50 text-[14px] font-medium hover:text-white transition-colors">
              See Pulse ↓
            </a>
          </div>
        </div>
      </section>

      <SubNav />

      {/* Sections — Treasurer Pulse leads (2026-05-21 Category C pivot).
       *  2026-05-21 kill-list sweep: sponsor management + cross-club benchmarks
       *  removed (deferred / killed per chalk-os/strategy/2026-05-21-os-decisions.md). */}
      <Section
        id="treasurer-pulse"
        hero
        eyebrow="Treasurer Pulse"
        heading="Close the books in minutes."
        desc="The treasurer of a 200-athlete club shouldn't need a 40-tab spreadsheet. Treasurer Pulse reconciles dues, registrations, and tournament fees in one view — and exports clean to QuickBooks when the board wants the numbers."
        bullets={[
          'Multi-team reconciliation — every Stripe transaction in one place',
          'QuickBooks export — chart-of-accounts ready, no manual rekeying',
          'Payment-risk signals — at-risk payers, failed cards, missed dues',
          'Multi-team waiver and registration oversight',
        ]}
        cta="See Treasurer Pulse"
        ctaHref="#club-plus"
        demo={<FinancesDemo />}
      />

      <div style={{ background: '#F8FAFC' }}>
        <Section
          id="dashboard"
          eyebrow="Club Overview"
          flip
          heading="Every team. One dashboard."
          desc="See all your teams, coaches, and upcoming events at a glance. No more separate apps per team, no more coaches texting you for updates."
          bullets={[
            'All teams in one view — records, rosters, next game',
            'Season revenue and dues collection rates',
            'Coach-level access controls per team',
            'Real-time notifications across your org',
          ]}
          cta="See the director portal"
          demo={<DashboardDemo />}
        />
      </div>

      <Section
        id="tryouts"
        eyebrow="Tryout Management"
        heading="Evaluate, rank, and offer — in the app."
        desc="Run a tryout from registration to offer letter without a clipboard or a shared Google Sheet. Score players, share notes between coaches, and send offers with one click."
        bullets={[
          'Digital scorecards for speed, arm, fielding, and hitting',
          'Real-time collaborative scoring across coaches',
          'Side-by-side prospect comparison',
          'In-app offer letters and waitlist management',
        ]}
        cta="Streamline your tryouts"
        flip
        demo={<TryoutsDemo />}
      />

      <div style={{ background: '#F8FAFC' }}>
        <Section
          id="tournaments"
          eyebrow="Tournaments"
          heading="Publish, fill, and run tournaments end-to-end."
          desc="Publish your tournament, collect entry fees, and generate brackets. Every game, every field, every result tracked in one portal. Hosting access is included with Coach Pro and Org; per-event pricing is paid by the host, never by parents."
          bullets={[
            'Multi-division setup with per-bracket entry fees',
            'Online registration with promo codes',
            'Bracket generation and scheduling',
            'Instant payouts via Stripe Connect',
            `Per-event pricing: ${PRICING_TOURNAMENT.hostingFee.display}${PRICING_TOURNAMENT.hostingFee.suffix} + ${PRICING_TOURNAMENT.perTeam.display}${PRICING_TOURNAMENT.perTeam.suffix} registered`,
          ]}
          cta="Run your first tournament"
          demo={<TournamentsDemo />}
        />
      </div>

      <div style={{ background: '#F8FAFC' }}>
        <Section
          id="finances"
          eyebrow="Finances"
          heading="All the money. None of the chasing."
          desc="Collect dues across every team from one portal. See exactly who's paid and who hasn't, and send reminders to overdue parents with one click. Money flows directly to your Stripe account — Chalk never holds funds."
          bullets={[
            'Org-wide revenue dashboard, broken out by team',
            'One-click payment reminders for overdue parents',
            'Tournament entry fee collection and instant payouts',
            'Full transaction history and CSV export',
            'Reduced platform fee for Org and Club Plus tiers',
          ]}
          cta="Take control of finances"
          flip
          demo={<FinancesDemo />}
        />
      </div>

      <Section
        id="embed"
        eyebrow="Embed & API"
        heading="Put live schedules on your own website."
        desc="Drop in a Chalk embed widget and your club site shows live scores and schedules automatically. Or wire it up via the REST API."
        bullets={[
          'Schedule, score, and standings widgets',
          'One-line JavaScript embed, no backend needed',
          'REST API for custom integrations',
          'Your domain, your brand — full white-label support',
        ]}
        cta="Explore the API"
        demo={<EmbedDemo />}
      />

      {/* Club Plus pricing band */}
      <section id="club-plus" className="py-24 px-6" style={{ background: '#FAFAF8' }}>
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest mb-4" style={{ background: 'rgba(7,20,40,0.06)', color: '#071428' }}>
              Club Plus — New
            </div>
            <h2 className="text-4xl font-black text-chalk-dark mb-4 tracking-tight">Built for the club layer.</h2>
            <p className="text-slate-600 text-[16px] max-w-[620px] mx-auto leading-relaxed">
              Org tier covers the operational basics. Club Plus is for the director and treasurer who need the books closed, the dues reconciled, and the board questions answered before the meeting starts.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-[920px] mx-auto">
            <div className="bg-white rounded-2xl p-7 border border-slate-200 flex flex-col">
              <p className="text-[11px] font-black text-slate-600 uppercase tracking-widest mb-2">Org</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-chalk-dark">$59</span>
                <span className="text-slate-600 text-sm">/month</span>
              </div>
              <p className="text-slate-500 text-sm mb-4">For orgs running 5+ teams.</p>
              <ul className="space-y-2 text-sm text-slate-600 mt-auto">
                <li>Org dashboard, all teams in one view</li>
                <li>Org-wide finances and reporting</li>
                <li>Shared resource calendar with conflict detection</li>
                <li>Registration & tryout intake</li>
                <li>Reduced platform fee on collections</li>
              </ul>
            </div>

            <div className="bg-chalkboard rounded-2xl p-7 border-2 border-chalkboard-raised flex flex-col relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-chalk-yellow text-chalkboard text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-widest leading-none">
                For directors
              </div>
              <p className="text-[11px] font-black text-chalk-yellow uppercase tracking-widest mb-2">Club Plus</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-white">$199</span>
                <span className="text-white/60 text-sm">/month</span>
              </div>
              <p className="text-white/50 text-sm mb-4">For club directors and treasurers running the whole show.</p>
              <ul className="space-y-2 text-sm text-white/80 mb-6">
                <li>Everything in Org</li>
                <li>Treasurer Pulse — close the books in minutes</li>
                <li>QuickBooks export, ready for the accountant</li>
                <li>Payment-risk signals — at-risk payers, failed cards, missed dues</li>
                <li>Multi-team waiver and registration oversight</li>
              </ul>
              <Link href="/org/signup" className="mt-auto block w-full text-center bg-chalk-yellow text-chalk-dark font-bold py-2.5 rounded-xl hover:bg-chalk-yellow/90 transition-colors text-sm">
                Talk to us about Club Plus
              </Link>
            </div>
          </div>

          <p className="text-center text-slate-500 text-xs mt-8">
            Parents never charged. Ever. Club Plus billing is to the club, not to families.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-chalkboard-grad">
        <div className="max-w-[640px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest mb-6" style={{ background: 'rgba(245,230,66,0.1)', border: '1px solid rgba(245,230,66,0.2)', color: '#F5E642' }}>
            For the people running the club
          </div>
          <h2 className="text-4xl font-black text-white mb-5 tracking-tight">Treasurer Pulse. Director portal. One operating system.</h2>
          <p className="text-white/40 text-[15px] mb-10 leading-relaxed">
            Clubs use Chalk to run teams, close the books, manage sponsors, and report to the board — all from one portal. Parents are never charged.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link href="/org/signup" className="bg-chalk-yellow-hover text-chalk-dark text-[15px] font-black rounded-full px-8 py-4 hover:bg-chalk-yellow transition-colors">
              Talk to us about Club Plus →
            </Link>
            <Link href="/directors/login" className="text-white/40 text-[14px] font-medium hover:text-white transition-colors">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </section>

      <HomepageFooter />
    </>
  )
}
